import { useEffect, useState } from "react";
import { FlaskConical, Leaf, ShieldCheck, Truck } from "lucide-react";
import { Link } from "react-router-dom";

import { LeadActions } from "@/components/LeadActions";
import { QuoteForm } from "@/components/QuoteForm";
import { SectionHeading } from "@/components/SectionHeading";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { contactDetails, imageAssets, productOptions } from "@/data/siteContent";
import { fetchBlogs } from "@/lib/api";

const icons = [Truck, ShieldCheck, Leaf, FlaskConical];

export default function HomePage({ content }) {
  const [blogs, setBlogs] = useState([]);
  const isHindi = content.locale === "hi";
  const trustChips = Array.isArray(content?.hero?.trustChips) ? content.hero.trustChips : [];
  const trustLine = Array.isArray(content?.hero?.trustLine) ? content.hero.trustLine : [];
  const metrics = Array.isArray(content?.hero?.metrics) ? content.hero.metrics : [];
  const trustBar = Array.isArray(content?.trustBar) ? content.trustBar : [];
  const buyerTypes = Array.isArray(content?.buyerTypes) ? content.buyerTypes : [];
  const featuredProducts = Array.isArray(content?.featuredProducts) ? content.featuredProducts : [];
  const whyChooseUs = Array.isArray(content?.whyChooseUs) ? content.whyChooseUs : [];
  const processSteps = Array.isArray(content?.processSteps) ? content.processSteps : [];
  const wholesaleBenefits = Array.isArray(content?.wholesale?.benefits) ? content.wholesale.benefits : [];
  const testimonials = Array.isArray(content?.testimonials) ? content.testimonials : [];

  useEffect(() => {
    fetchBlogs()
      .then((items) => setBlogs(items.slice(0, 3)))
      .catch(() => setBlogs([]));
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(22,101,52,0.18),_transparent_34%),linear-gradient(180deg,#fafaf9_0%,#ffffff_100%)]">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_0.95fr] lg:px-8 lg:py-24">
          <div className="flex flex-col justify-center">
            <span className="mb-4 inline-flex w-fit rounded-full border border-primary/15 bg-primary/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.24em] text-primary" data-testid="home-hero-eyebrow">
              {content.hero.eyebrow}
            </span>
            <h1 className="font-heading text-4xl font-bold leading-tight text-stone-900 sm:text-5xl lg:text-6xl" data-testid="home-hero-title">
              {content.hero.title}
            </h1>
            <p className="mt-6 max-w-2xl text-sm leading-7 text-stone-600 sm:text-base" data-testid="home-hero-description">
              {content.hero.description}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              {trustChips.map((chip) => (
                <Badge key={chip} className="rounded-full bg-white px-4 py-2 text-xs text-primary shadow-sm" data-testid={`home-hero-chip-${chip.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {chip}
                </Badge>
              ))}
            </div>

            <LeadActions labels={content.ctas} contact={contactDetails} quoteHref="#quote-request" prefix="hero" className="mt-8" />

            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-stone-500" data-testid="home-hero-trust-line">
              {trustLine.map((item, index) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="font-medium text-stone-600" data-testid={`home-hero-trust-line-item-${index + 1}`}>
                    {item}
                  </span>
                  {index < trustLine.length - 1 ? <span className="text-primary/40">•</span> : null}
                </div>
              ))}
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {metrics.map((item) => (
                <Card key={item.label} className="rounded-3xl border-stone-200 bg-white/85 shadow-lg shadow-stone-200/50">
                  <CardContent className="space-y-1 p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-400" data-testid={`hero-metric-label-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {item.label}
                    </p>
                    <p className="text-lg font-semibold text-stone-900" data-testid={`hero-metric-value-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {item.value}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -left-6 top-6 h-36 w-36 rounded-full bg-accent/20 blur-3xl" />
            <div className="absolute -right-4 bottom-6 h-40 w-40 rounded-full bg-primary/15 blur-3xl" />
            <div className="relative overflow-hidden rounded-[32px] border border-white/70 bg-white p-3 shadow-2xl shadow-stone-300/40">
              <img
                src={imageAssets.hero}
                alt="Fresh mushrooms cultivated at Sharda Agrotech"
                className="aspect-[4/4.2] w-full rounded-[26px] object-cover object-center"
                data-testid="home-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {trustBar.map((item, index) => {
            const Icon = icons[index];
            return (
              <Card key={item.title} className="h-full rounded-[28px] border-stone-200 bg-white shadow-sm transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-lg">
                <CardContent className="space-y-4 p-6">
                  <div className="inline-flex rounded-2xl bg-primary/10 p-3 text-primary" data-testid={`trust-card-icon-${index + 1}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-stone-900" data-testid={`trust-card-title-${index + 1}`}>
                    {item.title}
                  </h3>
                  <p className="text-sm leading-7 text-stone-600" data-testid={`trust-card-text-${index + 1}`}>
                    {item.text}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <SectionHeading eyebrow={isHindi ? "हम किनके लिए सप्लाई करते हैं" : "Who we serve"} title={isHindi ? "हर बढ़ते व्यवसाय के लिए ताज़ी मशरूम सप्लाई" : "Fresh mushroom supply for growing businesses"} description={content.wholesale.description} />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {buyerTypes.map((buyer, index) => (
            <div key={buyer} className="rounded-[24px] border border-primary/10 bg-primary/5 px-5 py-6 text-center text-sm font-semibold text-primary" data-testid={`buyer-type-card-${index + 1}`}>
              {buyer}
            </div>
          ))}
        </div>
        <LeadActions labels={content.ctas} contact={contactDetails} quoteHref="#quote-request" prefix="buyers" className="mt-8" />
      </section>

      <section className="bg-stone-50/80">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={isHindi ? "प्रमुख उत्पाद" : "Featured products"} title={content.productsPage.title} description={content.productsPage.description} />
          <div className="mt-10 grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
            {featuredProducts.map((product) => (
              <Card key={product.slug} className="flex h-full flex-col overflow-hidden rounded-[30px] border-stone-200 bg-white shadow-sm transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-xl">
                <img src={product.image} alt={product.title} className="aspect-[4/3] w-full object-cover object-center" data-testid={`featured-product-image-${product.slug}`} />
                <CardContent className="flex flex-1 flex-col space-y-4 p-6">
                  <h3 className="font-heading text-2xl font-semibold text-stone-900" data-testid={`featured-product-title-${product.slug}`}>
                    {product.title}
                  </h3>
                  <p className="text-sm leading-7 text-stone-600" data-testid={`featured-product-description-${product.slug}`}>
                    {product.summary}
                  </p>
                  {product.slug === "value-added-products" ? (
                    <a href="#quote-request" className="mt-auto inline-flex text-sm font-semibold text-primary" data-testid="featured-product-coming-soon-link">
                      {content.ctas.inquiryOnly}
                    </a>
                  ) : (
                    <Link to={`/products/${product.slug}`} className="mt-auto inline-flex text-sm font-semibold text-primary" data-testid={`featured-product-link-${product.slug}`}>
                      {content.ctas.visitProduct}
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
        <div>
          <SectionHeading eyebrow={isHindi ? "हमें क्यों चुनें" : "Why choose us"} title={content.about.title} description={content.about.intro} />
          <p className="mt-5 text-sm leading-7 text-stone-600" data-testid="home-about-supporting-text">
            {content.about.supporting}
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {whyChooseUs.map((item, index) => (
              <div key={item} className="rounded-[24px] border border-stone-200 bg-white px-5 py-5 text-sm font-semibold text-stone-700 shadow-sm" data-testid={`why-choose-card-${index + 1}`}>
                {item}
              </div>
            ))}
          </div>
        </div>

        <Card className="rounded-[32px] border-primary/10 bg-primary text-white shadow-2xl shadow-emerald-300/25">
          <CardContent className="space-y-6 p-8 sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-100" data-testid="founder-card-eyebrow">
              {isHindi ? "संस्थापक परिचय" : "Founder spotlight"}
            </p>
            <div>
              <h3 className="font-heading text-3xl font-semibold" data-testid="founder-name-text">
                {content.founder.name}
              </h3>
              <p className="mt-2 text-sm font-semibold text-emerald-100" data-testid="founder-title-text">
                {content.founder.title}
              </p>
            </div>
            <p className="text-sm leading-7 text-emerald-50" data-testid="founder-description-text">
              {content.founder.description}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-[24px] bg-white/10 p-5" data-testid="mission-card">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-100">{isHindi ? "मिशन" : "Mission"}</p>
                <p className="mt-3 text-sm leading-7 text-white">{content.mission}</p>
              </div>
              <div className="rounded-[24px] bg-white/10 p-5" data-testid="vision-card">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-100">{isHindi ? "विजन" : "Vision"}</p>
                <p className="mt-3 text-sm leading-7 text-white">{content.vision}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="bg-gradient-to-b from-stone-50 to-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={isHindi ? "हमारी प्रक्रिया" : "Our process"} title={isHindi ? "कम्पोस्ट से आपकी रसोई तक" : "From compost to your kitchen"} description={content.processIntro} />
          <div className="mt-10 grid gap-4 lg:grid-cols-5">
            {processSteps.map((step, index) => (
              <Card key={step.title} className="rounded-[28px] border-stone-200 bg-white">
                <CardContent className="space-y-4 p-6">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-sm font-bold text-accent" data-testid={`process-step-number-${index + 1}`}>
                    {index + 1}
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-stone-900" data-testid={`process-step-title-${index + 1}`}>
                    {step.title}
                  </h3>
                  <p className="text-sm leading-7 text-stone-600" data-testid={`process-step-text-${index + 1}`}>
                    {step.text}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="quote-request" className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <div className="space-y-6">
          <SectionHeading eyebrow={isHindi ? "थोक सप्लाई" : "Wholesale"} title={content.wholesale.title} description={content.wholesale.description} />
          <div className="grid gap-4">
            {wholesaleBenefits.map((item, index) => (
              <div key={item} className="rounded-[24px] border border-primary/10 bg-primary/5 px-5 py-5 text-sm font-medium text-stone-700" data-testid={`wholesale-benefit-${index + 1}`}>
                {item}
              </div>
            ))}
          </div>
        </div>
        <QuoteForm copy={{ ...content.quoteForm, submitQuote: content.ctas.submitQuote, submitContact: content.ctas.submitContact }} products={productOptions} language={content.locale} mode="quote" />
      </section>

      <section className="bg-primary">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={isHindi ? "प्रशंसापत्र" : "Testimonials"} title={content.testimonialsTitle} description={content.footerText} tone="light" />
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {testimonials.map((item, index) => (
              <Card key={item.name} className="h-full rounded-[28px] border-white/10 bg-white/95 shadow-xl shadow-emerald-950/10">
                <CardContent className="space-y-6 p-6">
                  <p className="text-sm leading-7 text-stone-600" data-testid={`testimonial-quote-${index + 1}`}>
                    “{item.quote}”
                  </p>
                  <div>
                    <p className="font-heading text-xl font-semibold text-stone-900" data-testid={`testimonial-name-${index + 1}`}>
                      {item.name}
                    </p>
                    <p className="text-sm text-stone-500" data-testid={`testimonial-role-${index + 1}`}>
                      {item.role}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading eyebrow={isHindi ? "ब्लॉग / रेसिपी" : "Blog / recipes"} title={content.blog.title} description={content.blog.description} />
          <Link className="text-sm font-semibold text-primary" to="/blog" data-testid="blog-preview-all-articles-link">
            {content.ctas.seeAllArticles}
          </Link>
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {blogs.map((blog) => (
            <Card key={blog.slug} className="rounded-[28px] border-stone-200 bg-white">
              <CardContent className="space-y-4 p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary" data-testid={`blog-card-category-${blog.slug}`}>
                  {blog.category}
                </p>
                <h3 className="font-heading text-2xl font-semibold text-stone-900" data-testid={`blog-card-title-${blog.slug}`}>
                  {blog.title}
                </h3>
                <p className="text-sm leading-7 text-stone-600" data-testid={`blog-card-excerpt-${blog.slug}`}>
                  {blog.excerpt}
                </p>
                <Link className="inline-flex text-sm font-semibold text-primary" to={`/blog/${blog.slug}`} data-testid={`blog-card-link-${blog.slug}`}>
                  {content.ctas.readArticle}
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}